import { useCallback, useEffect, useRef, useState } from "react";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { fetchTerminalCompletions, fetchTerminalSession } from "@/lib/api";
import { API_URL } from "@/lib/api-client";
import { isAbortError } from "@/lib/abort-error";
import { refreshEnvironmentFiles } from "@/lib/environment-files";
import {
	buildTerminalPtyStartMessage,
	buildTerminalPtyWsUrl,
	TerminalPtySession,
	type TerminalPtySocket,
} from "@/lib/terminal-pty";
import {
	COMMAND_SCREEN_RESET,
	formatTerminalPrompt,
	getTerminalMetaArrowSequence,
	getTerminalOutputScrollDirection,
	resetCommandScreen,
	shouldCopyTerminalSelection,
	terminalCommandOutcomeChunks,
	type TerminalOutputChunk,
	validateTerminalCommand,
} from "@/lib/terminal";
import {
	appendTerminalTranscript,
	clearTerminalDraft,
	clearTerminalTranscript,
	readTerminalDraft,
	readTerminalTranscript,
	type TerminalTranscriptEntry,
	writeTerminalDraft,
} from "@/lib/terminal-session-state";
import {
	historyNext,
	historyPrevious,
	INITIAL_HISTORY_NAV,
	type HistoryNavState,
} from "@/lib/input-history";
import {
	loadTerminalFont,
	TERMINAL_FALLBACK_FONT_FAMILY,
	TERMINAL_FONT_FAMILY,
	TERMINAL_FONT_SIZE,
	TERMINAL_LINE_HEIGHT,
} from "@/lib/terminal-font";
import { renderTerminalLineText, type LineEditorPrompt } from "@/lib/terminal-line-editor";
import { TerminalTranscriptRecorder } from "@/lib/terminal-transcript-recorder";
import { XtermLineEditor } from "@/lib/xterm-line-editor";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import { tokenReader } from "@/lib/css-tokens";

const ANSI_COLOR = {
	default: "\u001b[0m",
	error: "\u001b[31m",
	muted: "\u001b[90m",
	prompt: "\u001b[1;32m",
} as const;

export type TerminalSessionPhase = "prompt" | "running";

interface TerminalSessionViewProps {
	terminalId: string;
	active: boolean;
	/** 画面上で実際に見えているか。true になったタイミングでフィットとフォーカスを行う。 */
	visible: boolean;
	chatId: string | null;
	environmentId: string | null;
	/** コマンド完了ごとにファイルツリーを再取得するか。チャットに紐付かない単発セッション向け。 */
	refreshFilesOnExit: boolean;
	onPhaseChange: (terminalId: string, phase: TerminalSessionPhase) => void;
}

type FinishOutcome =
	| { type: "exit"; exitCode: number; cwd: string | null }
	| { type: "error"; message: string };

interface XtermSession {
	terminal: Terminal;
	fitAddon: FitAddon;
	editor: XtermLineEditor;
}

function createLineEditorPrompt(cwd: string | null): LineEditorPrompt {
	const text = formatTerminalPrompt(cwd);
	return { text, renderedText: `${ANSI_COLOR.prompt}${text}${ANSI_COLOR.default}` };
}

function writeTerminalChunks(terminal: Terminal, chunks: TerminalOutputChunk[]): void {
	for (const chunk of chunks) {
		terminal.write(`${ANSI_COLOR[chunk.color]}${chunk.text}${ANSI_COLOR.default}`);
	}
}

function createPtySocket(url: string): TerminalPtySocket {
	const ws = new WebSocket(url);
	ws.binaryType = "arraybuffer";
	const socket: TerminalPtySocket = {
		onopen: null,
		onmessage: null,
		onclose: null,
		send: (data) => ws.send(data as string | Uint8Array<ArrayBuffer>),
		close: (code) => ws.close(code),
	};
	ws.onopen = () => socket.onopen?.();
	ws.onmessage = (event) => socket.onmessage?.({ data: event.data as ArrayBuffer | string });
	ws.onclose = (event) => socket.onclose?.({ code: event.code });
	return socket;
}

function restoreTerminalTranscript(terminal: Terminal, entries: TerminalTranscriptEntry[]): void {
	for (const entry of entries) {
		const prompt = createLineEditorPrompt(entry.cwd);
		terminal.write(
			`${prompt.renderedText}${renderTerminalLineText(prompt.text, entry.command, terminal.cols)}\r\n`,
		);
		writeTerminalChunks(terminal, entry.chunks);
	}
}

/** 1つの terminalId に対応する xterm と PTY のライフサイクルを保持するビュー。 */
export function TerminalSessionView({
	terminalId,
	active,
	visible,
	chatId,
	environmentId,
	refreshFilesOnExit,
	onPhaseChange,
}: TerminalSessionViewProps) {
	const currentUserId = useCurrentUserStore((state) => state.id);
	const terminalContainerRef = useRef<HTMLDivElement>(null);
	const terminalSessionRef = useRef<XtermSession | null>(null);
	const historyRef = useRef<string[]>([]);
	const historyNavRef = useRef<HistoryNavState>(INITIAL_HISTORY_NAV);
	const executeRef = useRef<(command: string) => void>(() => undefined);
	const currentUserIdRef = useRef(currentUserId);
	const environmentIdRef = useRef(environmentId);
	const chatIdRef = useRef(chatId);
	const phaseRef = useRef<TerminalSessionPhase>("prompt");
	const ptySessionRef = useRef<TerminalPtySession | null>(null);
	const cwdRef = useRef<string | null>(null);
	const cwdRevisionRef = useRef(0);
	const [phase, setPhase] = useState<TerminalSessionPhase>("prompt");
	/** フォント読み込み後に xterm セッションを開いたか。フィット・フォーカスはこの後に行う。 */
	const [sessionReady, setSessionReady] = useState(false);

	currentUserIdRef.current = currentUserId;
	environmentIdRef.current = environmentId;
	chatIdRef.current = chatId;
	phaseRef.current = phase;

	const updatePhase = useCallback(
		(nextPhase: TerminalSessionPhase) => {
			phaseRef.current = nextPhase;
			setPhase(nextPhase);
			onPhaseChange(terminalId, nextPhase);
		},
		[onPhaseChange, terminalId],
	);

	const persistDraft = useCallback(
		(text: string) => {
			const userId = currentUserIdRef.current;
			const selectedEnvironmentId = environmentIdRef.current;
			const selectedChatId = chatIdRef.current;
			if (userId === "" || selectedEnvironmentId === null) return;
			if (text.length === 0) {
				clearTerminalDraft(userId, selectedEnvironmentId, selectedChatId, terminalId);
				return;
			}
			writeTerminalDraft(userId, selectedEnvironmentId, selectedChatId, terminalId, text);
		},
		[terminalId],
	);

	const persistTranscriptEntry = useCallback(
		(
			userId: string,
			selectedEnvironmentId: string,
			selectedChatId: string | null,
			entry: TerminalTranscriptEntry,
		) => {
			if (userId !== "") {
				appendTerminalTranscript(userId, selectedEnvironmentId, selectedChatId, terminalId, entry);
			}
		},
		[terminalId],
	);

	const refreshPromptCwd = useCallback(
		(envId: string, chatIdForFetch: string | null): (() => void) => {
			const requestedSession = terminalSessionRef.current;
			const requestedCwdRevision = cwdRevisionRef.current;
			const isCurrentRequest = (): boolean =>
				terminalSessionRef.current === requestedSession &&
				cwdRevisionRef.current === requestedCwdRevision;
			const abort = new AbortController();
			fetchTerminalSession(envId, chatIdForFetch, terminalId, abort.signal)
				.then((session) => {
					if (!isCurrentRequest()) return;
					cwdRef.current = session.cwd;
					terminalSessionRef.current?.editor.updatePrompt(createLineEditorPrompt(session.cwd));
				})
				.catch((caught: unknown) => {
					if (isAbortError(caught) || !isCurrentRequest()) return;
					console.error("fetchTerminalSession failed", caught);
					useToastStore
						.getState()
						.show({ type: "error", message: "作業ディレクトリの取得に失敗しました。" });
				});
			return () => abort.abort();
		},
		[terminalId],
	);

	const execute = useCallback(
		(command: string): void => {
			const session = terminalSessionRef.current;
			if (session === null) return;

			const showRecoverableError = (message: string): void => {
				session.editor.setEnabled(true);
				session.editor.finalizeLine();
				session.terminal.write(`${ANSI_COLOR.error}${message}\r\n${ANSI_COLOR.default}`);
				session.editor.beginLine(createLineEditorPrompt(cwdRef.current));
				session.editor.replaceBuffer(command);
				persistDraft(command);
			};

			const validationError = validateTerminalCommand(command);
			if (validationError) {
				showRecoverableError(validationError);
				return;
			}
			if (!environmentId) {
				showRecoverableError("実行するエージェントを選択してください。");
				return;
			}
			if (phaseRef.current === "running") return;

			const submittedEnvironmentId = environmentId;
			const submittedChatId = chatId;
			const submittedUserId = currentUserIdRef.current;
			const submittedCwd = cwdRef.current;
			historyRef.current.push(command);
			historyNavRef.current = INITIAL_HISTORY_NAV;
			session.editor.finalizeLine();
			session.editor.setEnabled(false);
			updatePhase("running");
			session.terminal.options.convertEol = false;

			const recorder = new TerminalTranscriptRecorder();
			let ptySession: TerminalPtySession | null = null;
			const finishCommand = (outcome: FinishOutcome): void => {
				const pty = ptySessionRef.current;
				if (terminalSessionRef.current !== session || pty === null || pty !== ptySession) return;
				ptySessionRef.current = null;
				pty.dispose();

				const recording = recorder.finish();
				resetCommandScreen(session.terminal, (trailingNewline) => {
					if (terminalSessionRef.current !== session) return;
					updatePhase("prompt");
					session.terminal.options.convertEol = true;
					// 接続断など、出力ゼロの失敗は未実行扱いにして入力を復元する。
					if (outcome.type === "error" && !recording.sawOutput) {
						showRecoverableError(outcome.message);
						return;
					}

					const outcomeChunks = terminalCommandOutcomeChunks(outcome);
					writeTerminalChunks(session.terminal, outcomeChunks);
					const resetChunk: TerminalOutputChunk = {
						text: `${COMMAND_SCREEN_RESET}${trailingNewline}`,
						color: "default",
					};
					// 新規チャットの確定中に終わったコマンドは、昇格後の保存先へ反映する。
					const completedChatId = chatIdRef.current;
					persistTranscriptEntry(submittedUserId, submittedEnvironmentId, completedChatId, {
						cwd: submittedCwd,
						command,
						chunks: [...recording.chunks, resetChunk, ...outcomeChunks],
					});
					if (outcome.type === "exit") {
						cwdRevisionRef.current++;
						if (outcome.cwd !== null) cwdRef.current = outcome.cwd;
					}

					session.editor.setEnabled(true);
					session.editor.beginLine(createLineEditorPrompt(cwdRef.current));
					const draft = readTerminalDraft(
						submittedUserId,
						submittedEnvironmentId,
						completedChatId,
						terminalId,
					);
					if (draft !== "") session.editor.replaceBuffer(draft);
					if (outcome.type === "exit") {
						if (outcome.cwd === null) refreshPromptCwd(submittedEnvironmentId, completedChatId);
						if (completedChatId !== null || refreshFilesOnExit)
							void refreshEnvironmentFiles(submittedEnvironmentId);
					}
				});
			};

			ptySession = new TerminalPtySession({
				buildUrl: (cols, rows) =>
					buildTerminalPtyWsUrl(submittedEnvironmentId, cols, rows, API_URL, window.location),
				socketFactory: createPtySocket,
				startMessage: buildTerminalPtyStartMessage(command, submittedChatId, terminalId),
				sink: {
					write: (data, onFlushed) => {
						recorder.note(data);
						session.terminal.write(data, onFlushed);
					},
				},
				callbacks: {
					onExit: (exitCode, cwd) => finishCommand({ type: "exit", exitCode, cwd }),
					onEnded: (message) => finishCommand({ type: "error", message }),
				},
			});
			ptySessionRef.current = ptySession;
			const container = terminalContainerRef.current;
			if (container !== null && container.clientWidth > 0 && container.clientHeight > 0)
				session.fitAddon.fit();
			ptySession.connect(session.terminal.cols, session.terminal.rows);
			session.terminal.focus();
		},
		[
			chatId,
			environmentId,
			persistDraft,
			persistTranscriptEntry,
			refreshFilesOnExit,
			refreshPromptCwd,
			terminalId,
			updatePhase,
		],
	);
	executeRef.current = execute;

	const openTerminalSession = useCallback(
		(container: HTMLDivElement, fontFamily: string): number => {
			terminalSessionRef.current?.terminal.dispose();
			container.replaceChildren();
			const color = tokenReader();
			const terminal = new Terminal({
				convertEol: true,
				cursorBlink: true,
				macOptionClickForcesSelection: true,
				scrollback: 5000,
				fontFamily,
				fontSize: TERMINAL_FONT_SIZE,
				lineHeight: TERMINAL_LINE_HEIGHT,
				theme: {
					background: color("--term-bg"),
					foreground: color("--term-fg"),
					red: color("--term-red"),
					brightBlack: color("--term-bright-black"),
					cursor: color("--term-cursor"),
					selectionBackground: color("--term-selection-bg"),
					scrollbarSliderBackground: color("--term-scrollbar"),
					scrollbarSliderHoverBackground: color("--term-scrollbar-hover"),
					scrollbarSliderActiveBackground: color("--term-scrollbar-active"),
				},
			});
			const fitAddon = new FitAddon();
			terminal.loadAddon(fitAddon);
			terminal.open(container);
			terminal.write("\u001b[?2004h");
			terminal.attachCustomKeyEventHandler((event) => {
				if (event.type !== "keydown") return true;
				const scrollDirection = getTerminalOutputScrollDirection({
					key: event.key,
					ctrlKey: event.ctrlKey,
					metaKey: event.metaKey,
					shiftKey: event.shiftKey,
					altKey: event.altKey,
					isComposing: event.isComposing,
				});
				if (scrollDirection !== null && phaseRef.current === "prompt") {
					event.preventDefault();
					terminal.scrollPages(scrollDirection === "up" ? -1 : 1);
					return false;
				}
				const metaArrowSequence = getTerminalMetaArrowSequence({
					key: event.key,
					ctrlKey: event.ctrlKey,
					metaKey: event.metaKey,
					shiftKey: event.shiftKey,
					altKey: event.altKey,
					isComposing: event.isComposing,
					applicationCursorKeys: terminal.modes.applicationCursorKeysMode,
				});
				if (metaArrowSequence !== null) {
					event.preventDefault();
					terminal.input(metaArrowSequence);
					return false;
				}
				const key = event.key.toLowerCase();
				if (key === "v" && (event.ctrlKey || event.metaKey)) return false;
				if (
					shouldCopyTerminalSelection({
						key: event.key,
						ctrlKey: event.ctrlKey,
						metaKey: event.metaKey,
						altKey: event.altKey,
						isComposing: event.isComposing,
						hasSelection: terminal.hasSelection(),
					})
				) {
					event.preventDefault();
					void navigator.clipboard
						.writeText(terminal.getSelection())
						.then(() => terminal.clearSelection())
						.catch((caught: unknown) => {
							console.error("clipboard write failed", caught);
							useToastStore
								.getState()
								.show({ type: "error", message: "選択範囲をコピーできませんでした。" });
						});
					return false;
				}
				return true;
			});
			const editor = new XtermLineEditor(terminal, {
				onSubmit: (command) => {
					persistDraft("");
					executeRef.current(command);
				},
				onHistory: (direction, currentText) => {
					const result =
						direction === "previous"
							? historyPrevious(historyNavRef.current, historyRef.current, currentText)
							: historyNext(historyNavRef.current, historyRef.current);
					if (result === null) return null;
					historyNavRef.current = result.state;
					persistDraft(result.text);
					return result.text;
				},
				onBufferChanged: (text) => {
					historyNavRef.current = INITIAL_HISTORY_NAV;
					persistDraft(text);
				},
				onBufferDiscarded: () => {
					historyNavRef.current = INITIAL_HISTORY_NAV;
					persistDraft("");
				},
				onScreenCleared: () => {
					const userId = currentUserIdRef.current;
					const selectedEnvironmentId = environmentIdRef.current;
					if (userId !== "" && selectedEnvironmentId !== null) {
						clearTerminalTranscript(userId, selectedEnvironmentId, chatIdRef.current, terminalId);
					}
				},
				onCompletionRequest: async (word, kind) => {
					const selectedEnvironmentId = environmentIdRef.current;
					if (selectedEnvironmentId === null) return null;
					try {
						const result = await fetchTerminalCompletions(
							selectedEnvironmentId,
							chatIdRef.current,
							terminalId,
							word,
							kind,
						);
						return result.candidates;
					} catch (caught: unknown) {
						console.error("fetchTerminalCompletions failed", caught);
						useToastStore
							.getState()
							.show({ type: "error", message: "補完候補の取得に失敗しました。" });
						return null;
					}
				},
			});
			terminal.onData((data) => {
				if (phaseRef.current === "running") {
					ptySessionRef.current?.sendInput(data);
					return;
				}
				editor.feed(data);
			});
			terminal.onResize(({ cols, rows }) => {
				if (phaseRef.current === "running") {
					ptySessionRef.current?.resize(cols, rows);
					return;
				}
				editor.handleResize();
			});
			terminalSessionRef.current = { terminal, fitAddon, editor };
			return window.requestAnimationFrame(() => {
				if (container.clientWidth > 0 && container.clientHeight > 0) fitAddon.fit();
			});
		},
		[persistDraft, terminalId],
	);

	const initializeTerminalSession = useCallback(
		(session: XtermSession, selectedEnvironmentId: string | null) => {
			if (selectedEnvironmentId === null) {
				session.editor.setEnabled(false);
				session.terminal.write(
					`${ANSI_COLOR.muted}実行するエージェントを選択してください。\r\n${ANSI_COLOR.default}`,
				);
				return;
			}
			session.editor.setEnabled(true);
			const userId = currentUserIdRef.current;
			const selectedChatId = chatIdRef.current;
			if (userId !== "")
				restoreTerminalTranscript(
					session.terminal,
					readTerminalTranscript(userId, selectedEnvironmentId, selectedChatId, terminalId),
				);
			session.editor.beginLine(createLineEditorPrompt(cwdRef.current));
			if (userId === "") return;
			const draft = readTerminalDraft(userId, selectedEnvironmentId, selectedChatId, terminalId);
			if (draft !== "") session.editor.replaceBuffer(draft);
		},
		[terminalId],
	);

	useEffect(() => {
		const container = terminalContainerRef.current;
		if (!container) return;
		let cancelled = false;
		let frame: number | null = null;
		const resizeObserver = new ResizeObserver(() => {
			if (container.clientWidth > 0 && container.clientHeight > 0)
				terminalSessionRef.current?.fitAddon.fit();
		});
		// フォント読み込み前に open すると fallback フォントの寸法で fit され下端が切れる。
		const font = loadTerminalFont(document.fonts);
		void font.ready.then((loaded) => {
			if (cancelled) return;
			frame = openTerminalSession(
				container,
				loaded ? TERMINAL_FONT_FAMILY : TERMINAL_FALLBACK_FONT_FAMILY,
			);
			const session = terminalSessionRef.current;
			if (session !== null) {
				initializeTerminalSession(session, environmentIdRef.current);
			}
			resizeObserver.observe(container);
			setSessionReady(true);
			if (loaded) return;
			// タイムアウト後に読み込めた場合、コンテナ寸法は変わらず ResizeObserver も発火しないため、
			// fontFamily の差し替えでセル寸法を再計測させてから明示的に fit し直す。
			void font.settled.then((lateLoaded) => {
				if (cancelled || !lateLoaded) return;
				const current = terminalSessionRef.current;
				if (current === null) return;
				current.terminal.options.fontFamily = TERMINAL_FONT_FAMILY;
				if (container.clientWidth > 0 && container.clientHeight > 0) current.fitAddon.fit();
			});
		});
		return () => {
			cancelled = true;
			if (frame !== null) window.cancelAnimationFrame(frame);
			resizeObserver.disconnect();
			ptySessionRef.current?.dispose();
			ptySessionRef.current = null;
			terminalSessionRef.current?.terminal.dispose();
			container.replaceChildren();
			terminalSessionRef.current = null;
			setSessionReady(false);
		};
	}, [initializeTerminalSession, openTerminalSession]);

	useEffect(() => {
		if (!visible || !active || !sessionReady) return;
		const frame = window.requestAnimationFrame(() => {
			const container = terminalContainerRef.current;
			if (container?.clientWidth && container.clientHeight)
				terminalSessionRef.current?.fitAddon.fit();
			terminalSessionRef.current?.terminal.focus();
		});
		return () => window.cancelAnimationFrame(frame);
	}, [active, visible, sessionReady]);

	useEffect(() => {
		if (environmentId === null) {
			cwdRef.current = null;
			return;
		}
		return refreshPromptCwd(environmentId, chatId);
	}, [chatId, environmentId, refreshPromptCwd]);

	return (
		<div
			data-testid={`terminal-session-${terminalId}`}
			data-terminal-running={phase === "running" ? "" : undefined}
			style={{ display: active ? "flex" : "none", flex: 1, minHeight: 0, flexDirection: "column" }}
		>
			<div
				ref={terminalContainerRef}
				data-testid="terminal-output"
				style={{
					flex: 1,
					minHeight: 48,
					padding: "8px 10px",
					background: "var(--term-bg)",
					overflow: "hidden",
				}}
			/>
		</div>
	);
}
